import React, { useRef, useEffect, useState, useContext } from 'react';
import ReactDOM from 'react-dom';
import gsap from 'gsap';
import { deburr } from 'lodash';
import os from '@incentivegames/ig-types/lib/constants/os';
import { communicationUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/communicationUtilities';
import { CardContext, ICardContext } from 'context/cardContext';
import CardImageTextArea from 'components/Card/components/CardImageTextArea';
import CardPlayerTeamAndPosition from 'components/Card/components/CardPlayerTeamAndPosition';
import Loading from 'components/Loading';
import revealImg from 'theme/assets/images/reveal.png';
import BonusIcon from 'components/BonusIcon';
import { BonusIdentifier } from 'constants/appEnums';
import { ReactComponent as PointsSvg } from 'theme/assets/images/points.svg';
import './CardAnimatedContent.scss';

const CardAnimatedContent = () => {
  const { player, decisionTimeBonus, newSigningBonus, shirtImage, canBeRevealed, zIndex, showPoints, points, revealed, onRevealFinishCallback, loading, triggerRevealAnimation } = useContext<
    ICardContext
  >(CardContext);

  // Dirty hack to get IOS version
  const iOSVersion = () => {
    let match: any = navigator.userAgent && navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (match === null) return false;
    let version = [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3] || 0, 10)];
    return parseFloat(version.join('.'));
  };

  // ToDo - remove all together when larger refactor to have card positions calculated using js only
  // Due to some varying support for the scale transform property. -
  // I've had to create two approaches for different browser specifically chrome and Safari. -
  // Chrome and the rest of the browsers will just use the scale property by it's self without -
  // scaling up fonts and image sizes. Safari does it a little differently by multiplying all pixel -
  // values by 4 to create larger initial assets then using the scale property to shrink it down -
  // to the correct size. It's done this way on Safari to avoid blurry images. The regular scaling on -
  // chrome is to avoid text going pixelated.
  const firefox: boolean = navigator.userAgent.includes('Firefox');
  const nativeAppCompatibilityMode = navigator.userAgent.includes('inApp');
  // ToDo refactor me into individual hacks e.g. firefoxCompatibilityMode, nativeAppCompatibilityMode, safariCompatibilityMode etc.

  /**
   * This check is tragically poor but there are no known better working alternatives.
   * Checking the user agent for safari etc returns the same thing for safari and chrome iOS emulator which require separate styles
   */
  const safariFirefoxCompatibilityMode =
    (navigator.userAgent && navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('CriOS') && !navigator.userAgent.includes('FxiOS')) ||
    (nativeAppCompatibilityMode && communicationUtilities.getOs() !== os.ANDROID);

  // Below IOS 13 on safari the image flip wasn't rendering correclty so we've added conditional styles for it
  const iosVersion = iOSVersion();
  const ios12AndBelowCompatibilityMode = typeof iosVersion === 'number' && iosVersion < 13;

  type CompatibilityMode = 'Safari' | 'Firefox' | 'Default';
  let styleCompatibilityMode: CompatibilityMode = 'Default';

  if (safariFirefoxCompatibilityMode) styleCompatibilityMode = 'Safari';
  if (firefox) styleCompatibilityMode = 'Firefox';
  interface ICardStyle {
    leftValue: string;
  }
  const stylesMap: Record<CompatibilityMode, ICardStyle> = {
    Default: { leftValue: 'unset' },
    Safari: { leftValue: '35%' },
    Firefox: { leftValue: '39%' }
  };

  const [addRevealedClass, setAddRevealClass] = useState<boolean>(false);
  // The component can render in different parts of the screen in the parent isn't finished -
  // rendering, so this hides the component until then.
  const [show, setShow] = useState<boolean>(false);
  const translateElement = useRef<any>(null);
  const scaleElement = useRef<any>(null);
  const rotateElement = useRef<any>(null);
  const calculatedZIndex = zIndex || 0;

  useEffect(() => {
    if (triggerRevealAnimation && translateElement?.current) {
      // create temporary overlay element
      const overlayElement = document.createElement('div');
      overlayElement.setAttribute('class', 'fives__overlay');
      // needs to be appended to end of container, wise is will cause layout issues.
      document.querySelector('.game-card-container')?.appendChild(overlayElement);

      const translateTL = gsap.timeline();
      const scaleAndRotateTl = gsap.timeline();
      const scaleTo = 0.7;

      gsap.set(translateElement.current, {
        zIndex: 9999
      });

      translateTL
        .to(
          translateElement.current,
          {
            duration: 1.4,
            transform: () => {
              let translate = 'unset';
              if (safariFirefoxCompatibilityMode) translate = 'translateX(-50%)';
              return translate;
            },
            left: () => {
              return stylesMap[styleCompatibilityMode].leftValue;
            },
            top: () => {
              return '60px';
            },
            ease: 'power2.inOut',
            overwrite: true
          },
          'sync-2'
        )
        .to(
          overlayElement,
          {
            duration: 1.4,
            opacity: 0.7,
            ease: 'power2.inOut',
            overwrite: true
          },
          'sync-2'
        );

      translateTL.play();
      scaleAndRotateTl
        .to(scaleElement.current, {
          duration: 1.4,
          scale: scaleTo,
          ease: 'power2.inOut',
          overwrite: true,
          onComplete: () => {
            translateTL.pause();
          }
        })
        .to(rotateElement.current, {
          ease: 'power2.inOut',
          delay: 0.4,
          duration: 1.5,
          rotationY: 180,
          onComplete: () => {
            translateTL.reverse(0).duration(0.5).delay(1);
            gsap.to(scaleElement.current, {
              delay: 1,
              duration: 0.5,
              scale: 0.25,
              ease: 'power2.inOut',
              overwrite: true,
              onComplete: () => {
                // Tidying up
                onRevealFinishCallback();
                setAddRevealClass(true);
                overlayElement.remove();
                // Resetting inline styles
                if (translateElement?.current) {
                  gsap.set([translateElement.current], { clearProps: 'all' });
                  gsap.set([translateElement.current], { zIndex: calculatedZIndex });
                }
              }
            });
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerRevealAnimation, safariFirefoxCompatibilityMode]);

  useEffect(() => {
    setAddRevealClass(revealed);
    setShow(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { displayName, name, lastName } = player;

  const renderDisplayName = () => (
    <div className={`card__player-name ${typeof shirtImage === 'string' && shirtImage.includes('reveal') ? 'card__player-name--no-image' : ''}`}>{deburr(displayName || name || lastName)}</div>
  );

  const classes = () => {
    let classes = 'card-animated-content';
    if (safariFirefoxCompatibilityMode) classes += ' card-animated-content--safari-firefox-compatibility-mode';
    if (ios12AndBelowCompatibilityMode) classes += ' card-animated-content--ios-12-and-below--safari-compatibility-mode';
    if (addRevealedClass) classes += ' card-animated-content--revealed';
    if (loading) classes += ' card-animated-content--loading';
    return classes;
  };

  const dynamicStyles = {
    zIndex: calculatedZIndex,
    opacity: show ? 1 : 0
  };

  const content = (
    <div ref={translateElement} className={classes()} style={dynamicStyles}>
      {loading && <Loading showText={false} />}
      <div ref={scaleElement} className="card-animated-content__scale">
        <div ref={rotateElement} className="card-animated-content__rotate">
          <div className="card-animated-content__front">
            <CardImageTextArea image={revealImg} addGlint={canBeRevealed} />
          </div>
          <div className="card-animated-content__back">
            {showPoints && (
              <div className="card-animated-content__points">
                <PointsSvg className="card-animated-content__points-svg" />
                {points}
              </div>
            )}
            {(decisionTimeBonus || newSigningBonus) && (
              <div className="card-animated-content__bonus">
                {decisionTimeBonus && <BonusIcon icon={BonusIdentifier.DecisionTime} active />}
                {newSigningBonus && <BonusIcon icon={BonusIdentifier.NewSigning} active />}
              </div>
            )}
            <CardImageTextArea animated={safariFirefoxCompatibilityMode} image={shirtImage} topHtml={renderDisplayName()} bottomHtml={<CardPlayerTeamAndPosition />} />
          </div>
        </div>
      </div>
    </div>
  );

  if (document.querySelector('.game-card-container')) return ReactDOM.createPortal(content, document.querySelector('.game-card-container')!);
  return null;
};

export default CardAnimatedContent;
